<template>
  <ValidationObserver ref="formValidation" v-slot="{ invalid }">
    <form :class="{ formValidated: validateFormCalled }">
      <h3 class="mt-6 mb-3 text-left" id="step1">1. Välj belopp</h3>
      <div class="flex flex-col items-start max-w-sm">
        <div class="flex w-full flex-row justify-center items-center">
          <button
            v-for="(amount, index) in amounts"
            type="button"
            :key="amount.value"
            class="btn shadow-sm flex-auto px-0 normal-case text-sm md:text-base xl:text-lg mt-4"
            :class="activeAmountClass(amount, index)"
            @click="setSelectedAmount(amount)"
          >
            {{amount.title}}
          </button>
        </div>
        <div class="mt-2" v-if="showCustomAmount">
          <TextInput v-model="currentAmount" name="Belopp" :customMessages="{ 'required': 'Ange en summa du vill stödja Naturarvet med' }" rules="required|minValue:50" type="tel" :maxlength="4" placeholder="Valfritt belopp"></TextInput>
        </div>
      </div>
      <div id="step2">
        <h3 class="mt-6 mb-3 text-left">2. Välj betalsätt</h3>
        <div class="flex flex-row w-full justify-center items-center">
          <!-- <button
            type="button" 
            class="btn shadow-sm flex-auto px-0 normal-case text-sm md:text-base xl:text-lg mt-4"
            :class="activePaymentMethodClass('autogiro')"
            @click="selectedPaymentMethod = 'autogiro'"
          >Autogiro</button> -->
          <button
            type="button" 
            class="btn ml-4 shadow-sm flex-auto px-0 normal-case text-sm md:text-base xl:text-lg mt-4"
            :class="activePaymentMethodClass('card')"
            @click="selectedPaymentMethod = 'card'"
          >Betalkort</button>
        </div>
      </div>
      <div v-show="cardPaymentMethod" class="mt-4 mx-auto">
        <h3 class="mt-6 mb-3 text-left">3. Ange kortuppgifter</h3>
        <div class="mt-4">
          <TextInput v-model="name" name="Namn" type="text" :rules="{ required: cardPaymentMethod }" placeholder="Namn"></TextInput>
        </div>
        <div class="mt-4">
          <StripeCard
            :amount="currentAmount"
            :name="name"
            :email="email"
            :futureUsage="true"
            ref="card"
            @confirmCardSuccess="confirmCardSuccess"
            @confirmCardFailure="confirmCardFailure"></StripeCard>
        </div>
        <div class="mt-4">
          <TextInput v-model="email" name="Email" type="email" autocomplete="email" :rules="{ required: cardPaymentMethod, email: cardPaymentMethod }" placeholder="E-post"></TextInput>
          <span class="text-smallest xl:text-md xxl:text-base text-gray-600">Bekräftelse skickas via e-post.</span>
        </div>
      </div>
      <div id="step3" v-if="selectedPaymentMethod === 'autogiro'">
        <h3 class="mt-6 mb-3 text-left">3. Skapa autogiro</h3>
        <span class="text-smaller text-gray-700 leading-tight">Du kan använda mobilt BankID eller annars skapa autogiro direkt hos din bank, alternativt fylla i en blankett och skicka in till oss.</span>
        <div class="relative">
          <div class="tabs relative flex border-b mt-4 text-smaller md:text-base">
            <div @click="withBankid = true" :class="withBankid ? 'active' : 'text-gray-500'" class="tab w-2/5 hover:bg-gray-200 border-black cursor-pointer p-2">
              <span class="px-2">Mobilt BankID</span>
            </div>
            <div @click="withBankid = false" :class="withBankid ? 'text-gray-500' : 'active'" class="tab w-2/5 hover:bg-gray-200 border-black cursor-pointer p-2">
              <span class="ml-2 px-2">Utan BankID</span>
            </div>
            <div class="absolute bottom-0 w-2/5 bg-gray-700 slider" role="presentation"></div>
          </div>
          <div v-if="!withBankid">
            <div class="mt-4">
              <label class="block text-gray-700 text-smaller font-bold" for="method">
                Välj sätt
              </label>
              <ValidationProvider v-slot="{ classes, errors }" id="method" rules="required|is_not:0">
                <div class="validation relative" :class="classes">
                  <select v-model="selectedNonBankIdMethod" placeholder="Välj sätt" class="text-input">
                    <option 
                      v-for="method in nonBankIdMethods"
                      :key="method.id"
                      :value="method.id"
                      :disabled="method.disabled"
                      :hidden="method.disabled"
                    >
                      {{method.name}}
                    </option>
                  </select>
                  <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg class="fill-current h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                  </div>
                </div>
                <span class="text-error">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
            <div v-if="selectedNonBankIdMethod === 'print'" class="mt-4">
              <TextInput v-model="email" name="Email" type="email" autocomplete="email" rules="required|email" placeholder="E-post"></TextInput>
              <span class="text-smallest text-gray-600">Vi skickar blanketten till din mejl.</span>
            </div>
            <div v-else-if="selectedNonBankIdMethod === 'bank'" class="mt-4 text-smaller text-gray-700">
              Du kan sätta upp autogiro som skogsfadder direkt i din internetbank. Gör då så här:
              <ul class="ml-4">
                <li class="list-outside list-decimal mt-1">Logga in i din internetbank.</li>
                <li class="list-outside list-decimal mt-1">Hitta menyalternativ för autogrio, det finns ofta under betala &amp; överföra.</li>
                <li class="list-outside list-decimal mt-1">Fyll i "naturarvet" som sökord för mottagare/företag, välj Insamlingsstiftelsen Naturarvet (bankgiro 901-1172) i sökresultatet.</li>
                <li class="list-outside list-decimal mt-1">Fyll i ditt 12-siffriga personnummer i betalnummer (ÅÅÅÅMMDDCCCC).</li>
                <li class="list-outside list-decimal mt-1">Fyll i belopp som ska dras den 28 varje månad i rutan för kompletterande eller speciell information.</li>
                <li class="list-outside list-decimal mt-1">Signera som ett vanligt bankärende.</li>
              </ul>
              <div class="mt-4">Stöter du på problem så tveka inte att ringa <a class="text-blue-500 font-bold" href="tel:0706484789">070-648 47 89</a>.</div>
            </div>
            <div v-else-if="selectedNonBankIdMethod === 'send'">
              <div class="flex items-end justify-between mt-4">
                <div class="flex-1">
                  <TextInput v-model="ssn" v-slot="{ valid }" name="Personnummer" :error="fetchAddressError" rules="length:12|pnr" type="tel" :maxlength="12" placeholder="ÅÅÅÅMMDDCCC">
                  <button type="button" class="relative btn btn-border mt-0 py-035 px-2 ml-2 normal-case" :disabled="!ssn || fetchingAddress || !valid" @click="fetchAddress">Hämta adress<div :class="[ fetchingAddress ? 'block' : 'hidden' ]" class="lds-dual-ring-sm absolute inset-y-0 inset-x-0 -mt-1 mx-auto w-8"></div>
                    </button>
                  </TextInput>
                </div>
              </div>
              <div class="mt-4">
                <TextInput v-model="name" name="Namn" type="text" rules="required" placeholder="Namn"></TextInput>
              </div>
              <div class="mt-4">
                <TextInput v-model="streetAddress" name="Gatuadress" type="text" rules="required" placeholder="Gatuadress"></TextInput>
              </div>
              <div class="mt-4 flex">
                <TextInput v-model="postalCode" name="Postnummer" type="tel" rules="required" placeholder="Postnummer"></TextInput>
                <div class="ml-2">
                  <TextInput v-model="city" name="Ort" type="text" rules="required" placeholder="Ort"></TextInput>
                </div>
              </div>
              <div class="mt-4">
                <TextInput v-model="email" name="Email" type="email" autocomplete="email" rules="required|email" placeholder="E-post"></TextInput>
                <span class="text-smallest text-gray-600">Bekräftelse skickas via e-post.</span>
              </div>
            </div>
          </div>
          <div v-else>
            <div class="mt-4">
              <TextInput v-model="ssn" name="Personnummer" rules="required|length:12|pnr" type="tel" :maxlength="12" placeholder="ÅÅÅÅMMDDCCC"></TextInput>
              <span class="text-smallest text-gray-600">Vi hämtar och sparar dina adressuppgifter.</span>
            </div>
            <div class="mt-4">
              <label class="block text-gray-700 text-smaller font-bold" for="bank">
                Bank
              </label>
              <ValidationProvider v-slot="{ classes, errors }" name="bank" rules="required|is_not:0">
                <div class="validation relative" :class="classes">
                  <select v-model="selectedBankId" placeholder="Välj bank" class="text-input">
                    <option 
                      v-for="bank in banks"
                      :key="bank.id"
                      :value="bank.id"
                      :disabled="bank.disabled"
                      :hidden="bank.disabled"
                      :selected="bank.id == selectedBankId"
                    >
                      {{bank.name}}
                    </option>
                  </select>
                  <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg class="fill-current h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                  </div>
                  <span class="text-error">{{ errors[0] }}</span>
                </div>
              </ValidationProvider>
            </div>
            <div class="mt-4" v-if="selectedBankId === 'manual'">
              <TextInput v-model="clearingNumber" name="Clearingnummer" type="tel" rules="required|numeric|length:4" :maxlength="4" placeholder="Clearingnummer"></TextInput>
            </div>
            <div class="mt-4" v-if="selectedBankId === 'manual'">
              <TextInput v-model="accountNumber" name="Kontonummer" type="tel" rules="required|numeric|max:12" :maxlength="12" placeholder="Kontonummer"></TextInput>
            </div>
            <div class="mt-4">
              <TextInput v-model="email" name="Email" type="email" autocomplete="email" rules="required|email" placeholder="E-post"></TextInput>
              <span class="text-smallest text-gray-600">Bekräftelse skickas via e-post.</span>
            </div>
            <div class="mt-4" v-if="showSelectBankAccount">
              <label class="block text-gray-700 text-smaller font-bold" for="bankAccount">
                Konto
              </label>
              <div class="relative">
                <select v-model="selectedBankAccount" placeholder="Välj bank" class="text-input">
                  <option 
                    v-for="(account, index) in bankAccounts"
                    :key="index"
                    :value="index"
                  >
                    {{account.Name}} - {{account.AccountNo.replace(/\d(?=\d{4})/g, "*")}}
                  </option>
                </select>
                <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg class="fill-current h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="text-center">
        <span v-if="error" v-html="error" class="text-error block bg-red-100 rounded py-2 mt-2 -mb-2"></span>
        <span v-if="invalid" class="absolute w-48 h-12 mt-6 z-50" @click="validateForm"></span>
        <button type="button" v-if="showSendAutogiroButton" :disabled="invalid || loading" @click="sendAutogiroMail" class="btn btn-primary normal-case relative">Slutför<div :class="[ loading ? 'block' : 'hidden' ]" class="lds-dual-ring-sm absolute inset-y-0 inset-x-0 mx-auto w-8"></div></button>
        <button type="button" v-if="showRegisterButton" :disabled="invalid || loading" @click="registerAutogiro" class="btn btn-primary normal-case relative">Slutför</button>
        <button type="button" v-if="cardPaymentMethod" class="btn btn-primary normal-case relative" :disabled="invalid || loading" @click="purchase">Slutför<div :class="[ loading ? 'block' : 'hidden' ]" class="lds-dual-ring-sm absolute inset-y-0 inset-x-0 mx-auto w-8"></div></button>
        <button type="button" v-if="showFetchAccountsButton" :disabled="invalid || loading" @click="fetchBankAcounts" class="btn btn-primary normal-case relative">Hämta konto med BankID</button>

        <span v-if="showIntegrityClause" class="block text-left mt-6 text-smallest text-gray-600">
          Genom att gå vidare samtycker jag till att mina personuppgifter behandlas i enlighet med <a class="text-blue-500 font-bold" href="/integritetspolicy">Naturarvets integritetspolicy</a>.
        <span v-if="selectedPaymentMethod == 'card'" class="text-left text-smallest text-gray-600">
          Samt att mina kortuppgifter får sparas så att fadderskapet kan dras varje månad på det belopp jag har valt
        </span></span>

        <span v-if="showBillectaIntegrityClause" class="block text-left mt-2 text-smallest text-gray-600">

          Genom att använda tjänsten godkänner du Användarvillkoren och Integritetspolicyn. Du kan läsa mer om hur vi använder dina ekonomiska uppgifter här <a class="text-blue-500 font-bold" href="https://app.billecta.com/ht/accountinformationservices">Billecta Payments användarvillkor</a>.
        </span>
      </div>

      <modal transition="overlay-fade"
             :clickToClose="false"
             :delay="100"
             :adaptive="true"
             name="bankidModal"
             height="auto"
             :scrollable="true"
             :maxWidth="512"
             classes="max-w-sm xl:max-w-md xxl:max-w-lg mt-2 pb-4 px-6 mb-8i"
             width="100%">
        <div key="active" class="pt-8 pb-3 flex flex-col text-center items-center" v-if="showActiveAutogiro">
          <h4 class="text-gray-800 text-xl">Du är redan Skogsfadder</h4>
          <div class="flex flex-row items-center mt-3">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-12 h-12" viewBox="0 0 323 323"><g fill="#9fcba5"><circle cx="121.224" cy="43.879" r="35.345"/><circle cx="72.817" cy="38.994" r="38.994"/><circle cx="154.264" cy="72.226" r="28.347"/><circle cx="38.241" cy="73.571" r="27.002"/><path d="M38.241 58.395h116.023v42.178H38.241z"/></g><path fill="#9fcba5" d="M212.895 215.338h26.946v95.317h-26.946z"/><path d="M308.741 197.065L248.835 74.969c-9.161-18.671-35.776-18.671-44.937 0l-59.906 122.096c-8.159 16.629 3.946 36.051 22.468 36.051h119.812c18.524 0 30.628-19.422 22.469-36.051z" fill="#2b7b57"/><path fill="#9fcba5" d="M122.145 191.058h26.946v119.596h-26.946z"/><path d="M181.979 157.214a49.498 49.498 0 003.283-17.756c0-27.417-22.226-49.643-49.643-49.643s-49.643 22.226-49.643 49.643c0 6.259 1.17 12.241 3.283 17.756-25.549 2.034-45.653 23.397-45.653 49.469 0 27.417 22.226 49.643 49.643 49.643 17.935 0 33.646-9.513 42.369-23.766 8.724 14.253 24.434 23.766 42.369 23.766 27.417 0 49.643-22.226 49.643-49.643 0-26.072-20.103-47.435-45.651-49.469z" fill="#49b86e"/><path d="M299.414 323H62.26c-6.819 0-12.346-5.528-12.346-12.346 0-6.819 5.528-12.346 12.346-12.346h237.154c6.819 0 12.346 5.528 12.346 12.346 0 6.819-5.527 12.346-12.346 12.346z" fill="#2b7b57"/></svg>
            <div class="text-left ml-2">
              <div class="leading-5">{{ activeAutogiro.amount }} kr/mån</div>
              <div v-if="activeAutogiro.nextWithdrawal" class="text-base text-gray-500">Nästa dragning {{ activeAutogiro.nextWithdrawal }}</div>
            </div>
          </div>
          <div class="mt-6 text-base">
            Om du vill ändra eller avsluta ditt skogsfadderskap ber vi dig kontakta givarservice på <a href="mailto:info@naturarvet.se">info@naturarvet.se</a>.
          </div>
          <div>
            <button type="button" @click="showActiveAutogiro = false" class="btn btn-border btn-secondary">Ok</button>
          </div>
        </div>
        <div key="signup" class="flex flex-col text-center items-center" v-else-if="signupSuccess">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-32 h-32 mt-6" viewBox="0 0 323 323"><g fill="#9fcba5"><circle cx="121.224" cy="43.879" r="35.345"/><circle cx="72.817" cy="38.994" r="38.994"/><circle cx="154.264" cy="72.226" r="28.347"/><circle cx="38.241" cy="73.571" r="27.002"/><path d="M38.241 58.395h116.023v42.178H38.241z"/></g><path fill="#9fcba5" d="M212.895 215.338h26.946v95.317h-26.946z"/><path d="M308.741 197.065L248.835 74.969c-9.161-18.671-35.776-18.671-44.937 0l-59.906 122.096c-8.159 16.629 3.946 36.051 22.468 36.051h119.812c18.524 0 30.628-19.422 22.469-36.051z" fill="#2b7b57"/><path fill="#9fcba5" d="M122.145 191.058h26.946v119.596h-26.946z"/><path d="M181.979 157.214a49.498 49.498 0 003.283-17.756c0-27.417-22.226-49.643-49.643-49.643s-49.643 22.226-49.643 49.643c0 6.259 1.17 12.241 3.283 17.756-25.549 2.034-45.653 23.397-45.653 49.469 0 27.417 22.226 49.643 49.643 49.643 17.935 0 33.646-9.513 42.369-23.766 8.724 14.253 24.434 23.766 42.369 23.766 27.417 0 49.643-22.226 49.643-49.643 0-26.072-20.103-47.435-45.651-49.469z" fill="#49b86e"/><path d="M299.414 323H62.26c-6.819 0-12.346-5.528-12.346-12.346 0-6.819 5.528-12.346 12.346-12.346h237.154c6.819 0 12.346 5.528 12.346 12.346 0 6.819-5.527 12.346-12.346 12.346z" fill="#2b7b57"/></svg>
          <span v-if="withBankid" class="block font-bold mt-4 text-green-800 max-w-full">Tack och välkommen som Skogsfadder i Naturarvet!</span>
          <span v-else class="block font-bold mt-4 text-green-800 max-w-full">Tack för att du vill bli Skogsfadder i Naturarvet!</span>
          <span v-if="selectedPaymentMethod === 'card'" class="block text-smaller xxl:text-lg mt-4 text-gray-600">Det angivna kortet kommer att användas för ditt Skogsfadderskap.</span>
          <span v-else-if="withBankid" class="block text-smaller mt-4 text-gray-600">Ditt autogiromedgivande är nu signerat och klart.</span>
          <span v-else class="block text-smaller mt-4 text-gray-600">Vi skickar hem en blankett för autogiro till dig. Fyll i den och skicka tillbaka den till oss.</span>
          <button type="button" class="btn btn-border btn-secondary mb-2" @click="navigateHome">Gå till startsidan</button>
        </div>
        <div key="fetching" v-else class="mx-auto flex flex-col text-center items-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="fill-current h-40 -my-6" style="" viewBox="0 0 595.3 841.9"><path fill="#479CBE" d="M241.9 423.6l13.2-83.2H241c-6.6 0-15.1-3.7-17.6-10.5-.8-2.3-2.7-10.2 8.2-17.9 3.9-2.7 6.4-5.7 6.9-8 .5-2.4-.1-4.5-1.8-6.1-2.4-2.3-7.1-3.6-13.1-3.6-10.1 0-17.2 5.8-17.9 10-.5 3.1 1.9 5.6 4 7.2 6.3 4.7 7.8 11.5 3.9 17.9-4 6.6-12.7 10.9-22 11h-14.4c-1.2 8.1-20.8 132.3-22.3 142.1H233c.7-4.4 4.3-27.9 9.2-58.9h-.3z"/><path fill="#00A5C3" d="M346.5 267.6H267l-10.6 67.3h13.5c7.4 0 14.4-3.4 17.4-8.3 1-1.6 1.4-3 1.4-4.3 0-2.8-1.9-4.9-3.8-6.3-5.2-3.9-6.3-8-6.3-10.9 0-.6 0-1.1.1-1.6 1.1-7.1 10.7-14.8 23.4-14.8 7.6 0 13.4 1.8 16.9 5.1 3.1 2.9 4.3 7 3.4 11.3-1.1 5.1-6.2 9.3-9.1 11.4-7.7 5.4-6.7 10.1-6.2 11.5 1.6 4.2 7.7 6.9 12.4 6.9H340v.1c28 .2 43 13.1 38.3 43.1-4.4 27.9-25.8 39.9-51.3 40.1l-10.1 64.4h14.9c62.9 0 114.3-40.4 124.4-104.2 12.5-79.2-37.7-110.8-109.7-110.8z"/><path fill="#235971" d="M346.5 267.6H267l-10.6 67.3h13.5c7.4 0 14.4-3.4 17.4-8.3 1-1.6 1.4-3 1.4-4.3 0-2.8-1.9-4.9-3.8-6.3-5.2-3.9-6.3-8-6.3-10.9 0-.6 0-1.1.1-1.6 1.1-7.1 10.7-14.8 23.4-14.8 7.6 0 13.4 1.8 16.9 5.1 3.1 2.9 4.3 7 3.4 11.3-1.1 5.1-6.2 9.3-9.1 11.4-7.7 5.4-6.7 10.1-6.2 11.5 1.6 4.2 7.7 6.9 12.4 6.9H340v.1c28 .2 43 13.1 38.3 43.1-4.4 27.9-25.8 39.9-51.3 40.1l-10.1 64.4h14.9c62.9 0 114.3-40.4 124.4-104.2 12.5-79.2-37.7-110.8-109.7-110.8z"/><g fill="#235971"><path d="M150.7 511.2h31.9c13.6 0 16.9 6.9 15.9 13.2-.8 5.1-4.3 8.9-10.3 11.4 7.6 2.9 10.6 7.4 9.5 14.5-1.4 8.9-9.1 15.5-19.2 15.5h-36.3l8.5-54.6zm21.1 22.6c6.2 0 9.1-3.3 9.7-7.2.6-4.2-1.3-7.1-7.5-7.1h-5.5l-2.2 14.3h5.5zm-3.4 23.6c6.4 0 10.1-2.6 11-7.9.7-4.6-1.9-7.3-8.1-7.3H165l-2.4 15.3h5.8zM242.4 566.2c-8.3.6-12.3-.3-14.3-3.9-4.4 2.7-9.3 4.1-14.5 4.1-9.4 0-12.7-4.9-11.8-10.3.4-2.6 1.9-5.1 4.3-7.2 5.2-4.5 18-5.1 23-8.5.4-3.8-1.1-5.2-5.8-5.2-5.5 0-10.1 1.8-18 7.2l1.9-12.4c6.8-4.9 13.4-7.2 21-7.2 9.7 0 18.3 4 16.7 14.6l-1.9 12c-.7 4.2-.5 5.5 4.2 5.6l-4.8 11.2zM228 547.4c-4.4 2.8-12.6 2.3-13.5 8.1-.4 2.7 1.3 4.7 4 4.7 2.6 0 5.8-1.1 8.4-2.9-.2-1-.1-2 .2-3.9l.9-6zM257.9 523.5h16.6l-.9 5.5c5.3-4.5 9.3-6.2 14.5-6.2 9.3 0 13.6 5.7 12.1 15l-4.3 27.9h-16.6l3.6-23.1c.7-4.2-.6-6.2-3.8-6.2-2.6 0-5 1.4-7.3 4.5l-3.8 24.7h-16.6l6.5-42.1zM313.1 511.2h16.6l-4.2 26.8 15.9-14.5h20.5l-20.4 18 16.4 24.2H337l-12.6-19.5h-.2l-3 19.5h-16.6l8.5-54.5z"/></g><g fill="#479CBE"><path d="M371.9 511.2H391l-8.4 54.5h-19.1l8.4-54.5zM400.3 511.2h27.3c21.1 0 27.2 15.3 25.2 28-1.9 12.4-11.7 26.5-30.2 26.5h-30.8l8.5-54.5zm17.7 41.5c9.3 0 14.4-4.6 15.9-14.3 1.1-7.2-1.1-14.3-11.4-14.3h-5.1l-4.4 28.6h5z"/></g></svg>
          <div v-if="fetchingAccounts" class="max-w-full">
            <span class="block font-bold">Öppna ditt Mobila BankID och legitimera dig mot din bank.</span>
            <span v-if="qrCode && !isOnMobile" class="block text-smaller mt-4 text-gray-600">Tryck på QR-symbolen i BankID appen och scanna QR-koden här nedan.</span>
            <span v-else class="block text-smaller mt-4 text-gray-600">Det kan ta upp till 45 sekunder att få återkoppling från din bank.</span>
            <img v-if="qrCode && !isOnMobile" :src="qrCode" class="mx-auto h-24 w-24 mt-4">
          </div>
          <div v-if="registeringAutogiro" class="max-w-full">
            <span class="font-bold">Öppna ditt Mobila BankID och signera ditt autogiromedgivande.</span>
            <div class="text-left">
              <span class="block text-smaller mt-4 font-bold">Betalningsmottagare</span>
              <span class="block text-smaller text-gray-600 leading-tight">Insamlingsstiftelsen Naturarvet</span>
              <span class="block text-smaller text-gray-600 leading-tight">Björksäter 1</span>
              <span class="block text-smaller text-gray-600 leading-tight">649 91 Sparreholm</span>
              <span class="block text-smaller mt-4 font-bold">Betalare</span>
              <span class="block text-smaller text-gray-600 leading-tight">{{ name }}, {{ ssn }}</span>
              <span class="block text-smallest text-gray-600 mt-4">Du godkänner då även <a target="_blank" class="text-blue-500 underline" href="/autogiro-medgivandetext">villkoren för autogiro</a>.</span>
            </div>
          </div>
          <div class="lds-dual-ring"></div>
        </div>
      </modal>
    </form>
  </ValidationObserver>
</template>

<script>
  import { ValidationProvider, ValidationObserver } from 'vee-validate'
  import axios from 'axios'
  import VueScrollTo from 'vue-scrollto'
  import TextInput from './TextInput.vue'
  import StripeCard from './StripeCard.vue'
  import Bugsnag from '@bugsnag/js'

  export default {
    name: 'SponsorSignup',
    components: {
      ValidationProvider, ValidationObserver, TextInput, StripeCard
    },
    created() {
      this.setSelectedAmount(this.amounts[0])
      let uri = window.location.search
      let params = new URLSearchParams(uri)
      this.detectMobile()
    },
    data() {
      return {
        selectedAmount: undefined,
        currentAmount: undefined,
        withBankid: true,
        ssn: undefined,
        name: undefined,
        streetAddress: undefined,
        postalCode: undefined,
        city: undefined,
        encryptedAddress: undefined,
        selectedBankId: '0',
        email: undefined,
        accountNumber: undefined,
        clearingNumber: undefined,
        billectaPublicId: undefined,
        error: undefined,
        fetchingAccounts: false,
        registeringAutogiro: false,
        signupSuccess: false,
        timer: undefined,
        timeWaitedForBankAccounts: 0,
        timeWaitedForRegister: 0,
        bankAccounts: [],
        selectedBankAccountIndex: undefined,
        amountError: undefined,
        qrCode: undefined,
        autoStartToken: undefined,
        isOnMobile: false,
        bankIdLaunched: false,
        selectedNonBankIdMethod: 0,
        loading: false,
        fetchingAddress: false,
        fetchAddressError: undefined,
        selectedPaymentMethod: 'card',
        validateFormCalled: false,
        activeAutogiro: { 'amount': 100, 'nextWithdrawal': '2020-09-25' },
        showActiveAutogiro: false,
        amounts: [
          { title: '150 kr', value: 150 }, 
          { title: '300 kr', value: 300 },
          { title: 'Valfritt', value: 0 },
        ],
        banks: [
          { name: 'Välj en bank', id: '0', disabled: true  },
          { name: 'Ange kontouppgifter manuellt', id: 'manual' },
          { name: 'DanskeBank', id: 'OEB' },
          { name: 'Handelsbanken', id: 'SHB' },
          { name: 'Ica', id: 'ICA' },
          { name: 'Lansforsakringar', id: 'LFB' },
          { name: 'Nordea', id: 'NB' },
          { name: 'SBAB', id: 'SBAB' },
          { name: 'SEB', id: 'SEB' },
          { name: 'Skandia', id: 'SKB' },
          { name: 'Sparbanken Syd', id: 'SYD' },
          { name: 'Swedbank', id: 'FSPA' },
          { name: 'Swedbank Sparbankerna', id: 'FSPASB' },
        ],
        nonBankIdMethods: [
          { name: 'Välj ett sätt', id: '0', disabled: true  },
          { name: 'Skriv ut blanketten själv', id: 'print' },
          { name: 'Blanketten skickas hem', id: 'send' },
          { name: 'Anmäl hos din bank', id: 'bank' },
        ],
      }
    },
    computed: {
      cardPaymentMethod() {
        return this.selectedPaymentMethod === 'card'
      },
      autogiroPaymentMethod() {
        return this.selectedPaymentMethod === 'autogiro'
      },
      showCustomAmount() {
        return this.selectedAmount && this.selectedAmount.value === 0
      },
      selectedBankAccount: {
        set(val) {
          this.selectedBankAccountIndex = val
          this.clearingNumber = this.bankAccounts[val].ClearingNo
          this.accountNumber = this.bankAccounts[val].AccountNo
        },
        get() {
          return this.selectedBankAccountIndex
        },
      },
      showSelectBankAccount() {
        return this.selectedBankId !== 'manual' && this.bankAccounts.length
      },
      showRegisterButton() {
        if (this.withBankid && this.selectedPaymentMethod === 'autogiro') {
          if (this.selectedBankId === 'manual') {
            return true
          } else if (this.accountNumber && this.clearingNumber) {
            return true
          }
        }
        return false
      },
      showFetchAccountsButton() {
        return this.selectedPaymentMethod === 'autogiro' && this.withBankid && !this.showRegisterButton
      },
      showSendAutogiroButton() {
        return this.autogiroPaymentMethod && !this.withBankid &&
          (this.selectedNonBankIdMethod === 'print' || this.selectedNonBankIdMethod === 'send')
      },
      showIntegrityClause() {
        return this.withBankid || this.showSendAutogiroButton || this.cardPaymentMethod
      },
      showBillectaIntegrityClause() {
        return this.autogiroPaymentMethod && this.withBankid
      },
    },
    watch: {
      fetchingAccounts() {
        this.showOrHideModal()
      },
      registeringAutogiro() {
        this.showOrHideModal()
      },
      signupSuccess() {
        this.showOrHideModal()
      },
      showActiveAutogiro() {
        this.showOrHideModal()
      },
    },
    methods: {
      showOrHideModal() {
        if (this.fetchingAccounts || this.registeringAutogiro || this.signupSuccess || this.showActiveAutogiro) {
          this.$modal.show('bankidModal');
        } else {
          this.$modal.hide('bankidModal');
        }
      },
      async purchase() {
        this.loading = true
        this.error = null
        try {
          const { data: { clientSecret, stripeCustomer } } = await axios.post(
            '/wp-json/naturarvet/v1/stripePaymentIntent',
            {
              name: this.name,
              email: this.email,
              amount: this.currentAmount,
            }
          )
          this.$refs.card.confirmCard(clientSecret, stripeCustomer)
        } catch (e) {
          Bugsnag.notify(e)
          this.$emit('confirmCardFailure',
             "Något gick fel när ditt kreditkort skulle användas. Vänligen försök igen eller kontakta givarservice på 070-648 47 89."
          )
        }
      },
      async confirmCardSuccess({ stripeCustomer, paymentIntent }) {
        try {
          await axios.post(
            '/wp-json/naturarvet/v1/addToSubscriptionPlan',
            {
              email: this.email,
              name: this.name,
              stripeCustomer: stripeCustomer,
              amount: this.currentAmount,
              paymentIntentId: paymentIntent.id,
              paymentMethod: paymentIntent.payment_method,
            }
          )
          this.loading = false
          this.signupSuccess = true
          document.body.classList.add('modal-block-scroll')
        } catch (e) {
          this.loading = false
          Bugsnag.notify(e)
          this.error = "Något gick fel, försök igen eller kontakta givarservice på 070-648 47 89"
        }
      },
      confirmCardFailure(message) {
        this.loading = false
        this.error = message
      },
      async checkForExistingAutogiro() {
        const { data: autogiroData } = await axios.get(`/wp-json/naturarvet/v1/autogiro?ssn=${this.ssn}`)
        if (autogiroData && autogiroData.active) {
          this.activeAutogiro = autogiroData
          this.showActiveAutogiro = true
          return true
        }
        return false
      },
      async fetchBankAcounts() {
        try {
          if (await this.checkForExistingAutogiro()) {
            return
          }
          this.fetchingAccounts = true
          document.body.classList.add('modal-block-scroll')
          this.error = null
          this.timeWaitedForBankAccounts = 0
          const { data } = await axios.post(
            '/wp-json/naturarvet/v1/bankAccount',
            { bankId: this.selectedBankId, ssn: this.ssn, email: this.email }
          )
          this.billectaPublicId = data.PublicId
          this.timer = setTimeout(this.fetchBankAccountsStatus, 1000)
        } catch(e) {
          Bugsnag.notify(e)
          this.error = "Det gick tyvärr inte att hämta dina kontouppgifter. Försök igen eller fyll i dina kontouppgifter manuellt."
          this.fetchingAccounts = false
          document.body.classList.remove('modal-block-scroll')
        }
      },
      async fetchBankAccountsStatus() {
        try {
          this.timeWaitedForBankAccounts = this.timeWaitedForBankAccounts + 1
          if (!this.billectaPublicId || this.timeWaitedForBankAccounts >= 60*2) {
            this.error = "Det gick tyvärr inte att hämta dina kontouppgifter. Försök igen eller fyll i dina kontouppgifter manuellt."
            this.fetchingAccounts = false
            document.body.classList.remove('modal-block-scroll')
            return
          }

          const { data } = await axios.get(
            `/wp-json/naturarvet/v1/bankAccount/${this.billectaPublicId}`,
          )
          if (data.Status === 'Success') {
            this.fetchingAccounts = false
            document.body.classList.remove('modal-block-scroll')
            this.bankAccounts = data.AccountNumbers
            this.selectedBankAccount = 0
            this.bankIdLaunched = false // To allow bankid to be launched again in case of using another bank
          } else if (data.Status === 'Failed') {
            this.fetchingAccounts = false
            document.body.classList.remove('modal-block-scroll')
            this.error = data.FailureMessage || "Det gick tyvärr inte att hämta dina kontouppgifter. Försök igen eller fyll i dina kontouppgifter manuellt."
            this.bankIdLaunched = false // To allow bankid to be launched again in case of failure
          } else {
            if (this.isOnMobile) {
              this.autoStartToken = data.BankIdAutostartToken
              if (!this.bankIdLaunched) {
                let redirectUrl = null //encodeURIComponent(`https://naturarvet.se/skogsfadder?email=${this.email}&ssn=${this.ssn}&selectedBankId=${this.selectedBankId}&billectaPublicId=${this.billectaPublicId}`)
                // Försök starta bankid appen
                this.bankIdLaunched = true
                if (this.autoStartToken) {
                  window.location = `bankid:///?autostarttoken=${this.autoStartToken}&redirect=null`
                } else {
                  window.location = `bankid:///?redirect=null`
                }
              }
            } else {
              this.qrCode = data.QR
            }
            this.timer = setTimeout(this.fetchBankAccountsStatus, 1000)
          }
        } catch(e) {
          Bugsnag.notify(e)
          this.error = "Det gick tyvärr inte att hämta dina kontouppgifter. Försök igen eller fyll i dina kontouppgifter manuellt."
          this.fetchingAccounts = false
          document.body.classList.remove('modal-block-scroll')
        }
      },
      detectMobile() {
        const ua = navigator.userAgent||navigator.vendor||window.opera
        if(/(android|bb\d+|meego).+mobile|blackberry|iemobile|ip(hone|od)|kindle|mobile.+firefox|opera m(ob|in)i|symbian|windows ce/i.test(ua)) {
          this.isOnMobile = true
        }
      },
      isiOs() {
        const ua = navigator.userAgent||navigator.vendor||window.opera
        return /ip(hone|od)/i.test(ua)
      },
      async registerAutogiro() {
        try {
          if (await this.checkForExistingAutogiro()) {
            return
          }
          this.registeringAutogiro = true
          document.body.classList.add('modal-block-scroll')
          await this.fetchAddress()
          // TODO: If no address is found for given ssn we need to ask for address
          this.error = null
          this.timeWaitedForRegister = 0
          const { data } = await axios.post(
            '/wp-json/naturarvet/v1/registerAutogiro',
            {
              ssn: this.ssn,
              clearingNumber: this.clearingNumber,
              accountNumber: this.accountNumber,
              bgMandatoryTextUrl: '/autogiro-medgivandetext',
              amount: this.currentAmount, 
            })
          this.referenceToken = data.ReferenceToken
          this.timer = setTimeout(this.registerAutogiroStatus, 1000)
        } catch(e) {
          Bugsnag.notify(e)
          this.error = "Din signering blev inte godkänd. Försök igen eller kontakta givarservice på 070-648 47 89 om felet kvarstår."
          this.registeringAutogiro = false
          document.body.classList.remove('modal-block-scroll')
        }
      },
      async registerAutogiroStatus() {
        try {
          this.timeWaitedForRegister = this.timeWaitedForRegister + 1
          if (!this.referenceToken || this.timeWaitedForBankAccounts >= 60*2) {
            this.registeringAutogiro = false
            document.body.classList.remove('modal-block-scroll')
            this.error = "Något gick fel vid signering med BankId. Försök igen eller kontakta givarservice på 070-648 47 89 om felet kvarstår."
            return
          }
          const { data } = await axios.get(
            `/wp-json/naturarvet/v1/registerAutogiro/${this.referenceToken}`,
          )
          if (data.Status === 'Complete') {
            await axios.post(
              `/wp-json/naturarvet/v1/finalizeAutogiro`,
              {
                signature: data.Signature,
                ssn: this.ssn,
                created: data.Created,
                email: this.email,
                clearingNumber: this.clearingNumber,
                accountNumber: this.accountNumber,
                bank: this.selectedBankId,
                amount: this.currentAmount,
                encryptedAddress: this.encryptedAddress,
              }
            )
            this.registeringAutogiro = false
            document.body.classList.remove('modal-block-scroll')
            this.signupSuccess = true
            document.body.classList.add('modal-block-scroll')
          } else if (data.Status === 'Error' || data.Stauts === 'NoClient') {
            this.registeringAutogiro = false
            document.body.classList.remove('modal-block-scroll')
            this.error = "Din signering blev inte godkänd. Försöka igen eller kontakta givarservice på 070-648 47 89 om felet kvarstår."
          } else {
            this.timer = setTimeout(this.registerAutogiroStatus, 1000)
          }
        } catch(e) {
          Bugsnag.notify(e)
            this.error = "Din signering blev inte godkänd. Försöka igen eller kontakta givarservice på 070-648 47 89 om felet kvarstår."
          this.registeringAutogiro = false
          document.body.classList.remove('modal-block-scroll')
        }
      },
      async sendAutogiroMail() {
        this.loading = true
        try {
          await axios.post(
            `/wp-json/naturarvet/v1/sendAutogiroMail`,
            {
              ssn: this.ssn,
              name: this.name,
              address: this.streetAddress,
              postalCode: this.postalCode,
              city: this.city,
              email: this.email,
              encryptedAddress: this.encryptedAddress,
              method: this.selectedNonBankIdMethod,
            }
          )
          this.loading = false
          this.signupSuccess = true
          document.body.classList.add('modal-block-scroll')
        } catch (e) {
          this.loading = false
          Bugsnag.notify(e)
          this.error = "Något gick fel när vi skulle skicka din autogiroblankett. Försök igen eller kontakt givarservis på 070-648 47 89."
        }
      },
      async validateForm(e) {
        this.validateFormCalled = true
        const { valid } = await this.$refs.formValidation.validate(e)
        if (!valid) {
          if (this.showCustomAmount) {
            VueScrollTo.scrollTo('#step1')
          } else {
            VueScrollTo.scrollTo('#step3')
          }
          return false
        }
        return true
      },
      validCurrentAmount(error) {
        return this.currentAmount && error == null
      },
      setSelectedAmount(amount) {
        this.amountError = null
        this.selectedAmount = amount
        this.currentAmount = amount.value > 0 ? amount.value : null
      },
      setCustomAmount(amount) {
        this.currentAmount = amount
      },
      activeAmountClass(amount, index) {
        const mlClass = index > 0 && 'ml-4'
        if (!this.selectedAmount) return [ mlClass, 'btn-border', 'hover:bg-gray-200' ]

        return this.selectedAmount.value === amount.value ?
          [ mlClass, 'btn-primary' ] : [ mlClass, 'btn-border', 'hover:bg-gray-200' ]
      },
      activePaymentMethodClass(method) {
        return this.selectedPaymentMethod == method ? 
          [ 'btn-primary' ] : [ 'btn-border', 'hover:bg-gray-200' ]
      },
      navigateHome() {
        window.location.href = '/'
      },
      async fetchAddress() {
        try {
          this.fetchingAddress = true
          this.error = undefined
          this.fetchAddressError = undefined
          const { data } = await axios.get(
            `/wp-json/naturarvet/v1/fetchAddress/${this.ssn}`,
          )
          this.fetchingAddress = false
          if (!(data && data.name && data.address)) {
            this.fetchAddressError = "Hittade ingen adress för det angivna personnummret"
            return
          }
          this.name = data.name
          this.streetAddress = data.address
          this.postalCode = data.postalCode
          this.city = data.city
          this.encryptedAddress = data.encryptedAddress
        } catch (e) {
          Bugsnag.notify(e)
          this.fetchingAddress = false
          this.fetchAddressError = "Kunde inte hämta adressen för angivet personnummer, försök igen senare."
        }
      },
    },
  }
</script>

<style lang="scss" scoped>
  .fade-enter-active {
    transition: opacity .5s;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }
  .slider {
    height: 3px;
    transition: left 0.25s;
  }
  .tab:first-child.active ~ .slider {
    left: 0;
  }
  .tab:nth-child(2).active ~ .slider {
    left: 40%;
  }
</style>
